import { useEffect, useMemo, useState } from 'react';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Contact, Entity, UserAgent } from '../__generated__/data-contracts';
import { AssignEntity, EntityCreate, EntityInfoEdit } from '@bumble/app/components/entities';
import { PageLoading } from '@bumble/app/components/PageLoading';
import SplitPanes from '../components/SplitPanes';
import BumbleTabs from '../components/BumbleTabs';
import MultipleSelections from '../components/MultipleSelections';
import { EntityDetails } from '../components/entities/EntityDetails';
import { apiError } from '../utils/api-error';

const columns: GridColDef[] = [
  {
    field: 'entity_name',
    headerName: 'Name',
    width: 200,
    disableColumnMenu: true,
  }, {
    field: 'parent_entity_name',
    headerName: 'Parent Entity Name',
    width: 300,
    disableColumnMenu: true,
  }, {
    field: 'description',
    headerName: 'Description',
    width: 500,
    disableColumnMenu: true,
  }, {
    field: 'email',
    headerName: 'Alert Message Emails',
    width: 500,
    disableColumnMenu: true,
  }, {
    field: 'entity_id',
    headerName: 'Entity ID',
    width: 300,
    disableColumnMenu: true,
  }
];

const columnVisible = {
  entity_name: true,
  entity_id: true,
  description: true,
  email: true,
  parent_entity_name: true
};

export const Entities = () => {
  const api = useApi();

  const [showEntityCreate, setShowEntityCreate] = useState(false);
  const [showAssignEntity, setShowAssignEntity] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [detailData, setDetailData] = useState<Entity | undefined>(undefined);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [assignedContacts, setAssignedContacts] = useState<Contact[]>([]);

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const { isLoading, data, refetch } = useQuery(
    ['entities'],
    api.getEntityApiV1GetEntityGet.bind(null, {}),
    { enabled: true },
  );

  const removeEntity = useMutation(api.removeEntityApiV1RemoveEntityPost, {
    onSuccess() {
      toast.success('Successfully removed an entity.');
      setLoadingModalOpen(false);
      refetch();
    },
    onError(error: any) {
      if (Array.isArray((error as any).error.detail)) {
        (error as any).error.detail.forEach((el: any) =>
          toast.error(el.message, { autoClose: false, hideProgressBar: true }),
        );
      } else {
        toast.error((error as any).error.detail, { autoClose: false, hideProgressBar: true });
      }
      setLoadingModalOpen(false);
    },
  });

  const getContacts = useMutation(api.getContactApiV1GetContactGet, {
    onSuccess: (data) => {
      setAssignedContacts(data?.data);
    },
    onError: apiError,
  });

  const refetchAssignedContacts = () => {
    getContacts.mutate({ entity_id: selectedRowItem });
  };

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
      setDetailData(data?.data.filter(item => item.entity_id === selectedRowItem)[0]);
      getContacts.mutate({ entity_id: selectedRowItem });
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem, data]);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const handleCreateEntity = () => {
    setShowEntityCreate(true);
  };

  const handleAssignEntity = () => {
    setShowAssignEntity(true);
  }

  const handleDelete = () => {
    setShowConfirmDelete(true);
  };

  const handleEdit = () => {
    setShowEditDialog(true);
  };

  const handleEntityDelete = () => {
    setLoadingModalOpen(true);
    removeEntity.mutate({
      entity_id: selectedItemId,
    });
  };

  return (
    <PageContent>
      <PageHeader
        pageName="Entities"
        count={data?.data?.length || 0}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <SplitPanes top = {
          <Card elevation={0}>
            <CardContent>
              <DataTableGrid
                rows={
                  data?.data.map((item) => {
                    return {
                      ...item,
                      id: item.entity_id,
                    };
                  }) || []
                }
                columns={columns}
                handleSelectionChange={handleSelectionChange}
                rowSelectionModel={rowSelectionModel}
                itemsSelected={rowSelectionModel.length === 1}
                newLabel="Add Entity"
                newOnClick={handleCreateEntity}
                columnVisibilityModel={columnVisible}
                getRowId={(row: UserAgent) => row?.user_agent_id}
                refreshData={() => { refetch(); }}
                menuItems={[
                  {
                    label: 'Edit Entity',
                    icon: <EditIcon color="info" fontSize="small" />,
                    onClick: handleEdit,
                  },
                  {
                    label: 'Assign Parent Entity',
                    icon: <EditIcon color="info" fontSize="small" />,
                    onClick: handleAssignEntity,
                    hasDivider: true
                  },
                  {
                    label: 'Delete Entity',
                    icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                    onClick: handleDelete,
                    hasDivider: true,
                  },
                ]}
              />
            </CardContent>
          </Card>
        } bottom = {
          <Card elevation={0} sx={{ mt: 5 }}>
            <CardContent>
              {rowSelectionModel.length === 1 ? (
                <BumbleTabs
                  tabs={[{
                    el: <EntityDetails
                          data={assignedContacts}
                          refetchData={refetchAssignedContacts}
                        />,
                    label: 'Contacts',
                    id: 0,
                  }]}
                />
              ) : (
                <MultipleSelections count={rowSelectionModel.length} />
              )}
            </CardContent>
          </Card>
        }>
        </SplitPanes>
      )}
      {showEntityCreate && (
        <DialogStandard
          open={showEntityCreate}
          handleClose={() => setShowEntityCreate(false)}
          title="Add Entity"
        >
          <EntityCreate
            onSubmit={() => {
              setShowEntityCreate(false);
              refetch();
            }}
          />
        </DialogStandard>
      )}
      {showAssignEntity && detailData && (
        <DialogStandard
          open={showAssignEntity}
          handleClose={() => setShowAssignEntity(false)}
          title="Assign entity"
        >
          <AssignEntity
            onSubmit={() => {
              setShowAssignEntity(false);
              refetch();
            }}
            entityId={selectedItemId}
            entityName={detailData.entity_name || ''}
            parentName={detailData.parent_entity_name || ''}
          />
        </DialogStandard>
      )}
      {showEditDialog && detailData && (
        <DialogStandard
          open={showEditDialog}
          handleClose={() => setShowEditDialog(false)}
          title="Edit Entity Info"
        >
          <EntityInfoEdit
            onSubmit={() => {
              setShowEditDialog(false);
              refetch();
            }}
            entityId={detailData?.entity_id}
            entityName={detailData?.entity_name}
            description={detailData?.description || ''}
            email={detailData?.email || ''}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleEntityDelete}
          title="Delete Entity?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}?
          Please note that this action is irreversible and will result in the permanent removal of Entity.`}
          confirmationPhrase={selectedItemId}
        />
      )}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </PageContent>
  );
};
